import React, { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { CalendarDays, Users, Wallet, TrendingUp, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

function Stat({ label, value, sub, icon: Icon, tid }) {
  return (
    <div className="card-sharp p-6" data-testid={tid}>
      <div className="flex items-start justify-between mb-4">
        <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)]">{label}</div>
        <Icon className="w-4 h-4 text-[var(--text-2)]" />
      </div>
      <div className="font-serif text-3xl">{value}</div>
      {sub && <div className="text-xs text-[var(--text-2)] mt-2 mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { fmt, currency, convert } = useAuth();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/events").then((r) => setEvents(r.data));
  }, []);

  const chartData = events.slice(0, 6).map((e) => ({
    name: e.name?.slice(0, 14) || "—",
    budget: 0,
    actual: 0,
  }));

  return (
    <div className="max-w-7xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-2">Overview</div>
        <h1 className="font-serif text-4xl">Operational Console</h1>
        <p className="text-sm text-[var(--text-2)] mt-3 max-w-2xl">
          Aggregate ledger view across all productions. Currency: <span className="mono">{currency}</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Events" value={data?.events_count ?? "—"} sub="Total productions" icon={CalendarDays} tid="stat-events" />
        <Stat
          label="Budget Forecast"
          value={data ? fmt(data.budget_total) : "—"}
          sub="Sum of all budgets"
          icon={Wallet}
          tid="stat-budget"
        />
        <Stat
          label="Actual Costs"
          value={data ? fmt(data.actual_total) : "—"}
          sub="Sum of all actuals"
          icon={Wallet}
          tid="stat-actual"
        />
        <Stat
          label="Revenue"
          value={data ? fmt((data.sales_total || 0) + (data.sponsors_total || 0)) : "—"}
          sub="Sales + Sponsorships"
          icon={TrendingUp}
          tid="stat-revenue"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-sharp p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Budget vs Actual (per event)</h3>
            <span className="text-[10px] uppercase tracking-widest text-[var(--text-2)]">Recent</span>
          </div>
          <div style={{ height: 280 }}>
            {events.length ? (
              <EventsBudgetChart events={events} currency={currency} convert={convert} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-[var(--text-2)]">
                No events yet.{" "}
                <Link to="/events" className="underline ml-1">
                  Create one
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="card-sharp p-6">
          <h3 className="font-serif text-xl mb-4">System</h3>
          <ul className="text-sm space-y-3">
            <li className="flex justify-between">
              <span className="text-[var(--text-2)]">Users</span>
              <span className="mono">{data?.users_count ?? "—"}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-[var(--text-2)]">Pending approval</span>
              <span className="mono text-[var(--accent)]">{data?.pending_users ?? 0}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-[var(--text-2)]">Sponsorships</span>
              <span className="mono">{data ? fmt(data.sponsors_total) : "—"}</span>
            </li>
          </ul>
          {data?.pending_users > 0 && (
            <Link
              to="/admin/users"
              className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--accent)]"
            >
              <AlertCircle className="w-3 h-3" /> Approve pending
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function EventsBudgetChart({ events, currency, convert }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      const out = [];
      for (const e of events.slice(0, 6)) {
        try {
          const { data } = await api.get(`/events/${e.id}/summary`);
          out.push({
            name: (e.name || "—").slice(0, 12),
            Budget: convert(data.budget_total || 0),
            Actual: convert(data.actual_total || 0),
          });
        } catch {}
      }
      setRows(out);
    })();
  }, [events, currency]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows}>
        <CartesianGrid stroke="#EBE8E0" vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#4A4A4A", fontFamily: "IBM Plex Mono" }} />
        <YAxis tick={{ fontSize: 11, fill: "#4A4A4A", fontFamily: "IBM Plex Mono" }} />
        <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #EBE8E0" }} />
        <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} />
        <Bar dataKey="Budget" fill="#3A4B3A" />
        <Bar dataKey="Actual" fill="#C05A45" />
      </BarChart>
    </ResponsiveContainer>
  );
}
